import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { orderBy } from 'lodash';
import { rd } from '@passionware/monads';

import { openModal } from 'store/v1/ui/ui.actions.js';
import { createBooking } from 'store/v1/bookings/bookings.actions.js';

import { createBookingObject } from 'v1/helpers/byModel/BookingHelper';
import { getResourceSlotRate } from 'v1/helpers/byModel/ResourceSlotHelper';
import { createNewEventObject } from 'v1/helpers/byModel/EventHelper';

import BookResourceFlyoverBookingContents from './BookResourceFlyoverBookingContents';
import { appScopeUtils } from 'v5/core/app-scope/appScope.types';
import { myServices } from 'v5/services/services.connected.app.js';
import { useSimpleEventSubscription } from '@passionware/simple-event';

const BookResourceFlyoverBooking = ({ slot, resourceId, saveSignal }) => {
  const dispatch = useDispatch();
  const bookingTypes = useSelector(state => state.booking_types);
  const resource = rd.getOrElse(
    myServices.resourceService.useItem(resourceId),
    {}
  );

  const scopeRd = myServices.appScopeService.useResolvedAppScope();
  const fieldsRequest = rd.tryMap(scopeRd, resolved => ({
    type: 'resource',
    subtypeId: resource.resource_type_id,
    scope: appScopeUtils.identityFromResolved(resolved)
  }));
  const fieldsRd =
    myServices.metastructureMappingService.useMetastructure(fieldsRequest);
  const fields = rd.getOrElse(rd.useLastWithPlaceholder(fieldsRd), []);
  const resourceFieldHasRate = fields.some(field => field.name === 'rates');
  const [event, setEvent] = useState();

  const getInitialRate = () => {
    if (resourceFieldHasRate) {
      const estRate = getResourceSlotRate(slot);

      if (estRate?.amount) {
        return estRate;
      }

      if (resource?.rates?.length > 0) {
        const resourceRate = orderBy(resource.rates, 'order').find(
          r => !r.archived
        );
        if (resourceRate) {
          return resourceRate.rate || resourceRate;
        }
      }
    }

    return {};
  };

  const [rate, setRate] = useState({});

  useEffect(() => {
    const newRate = getInitialRate();
    setRate(newRate);
  }, [resource.rates, resourceFieldHasRate, slot]);

  const getBookingRate = () => {
    return rate
      ? {
          rate_currency: rate.currency,
          rate_amount: rate.amount,
          rate_interval: rate.interval || 'DAILY'
        }
      : null;
  };

  // TODO: this is mostly ripped out of ResourceStatusActions
  const saveBooking = async status => {
    const newBooking = {
      ...createBookingObject(bookingTypes, {
        event,
        resource: await myServices.resourceService.ensureItem(resourceId),
        slot,
        status
      }),
      ...getBookingRate()
    };

    dispatch(createBooking(newBooking));
  };

  useSimpleEventSubscription(saveSignal.addListener, saveBooking);

  const openCreateBooking = () => {
    dispatch(
      openModal('ResourceEventsModal', {
        resourceSlotId: slot.id,
        resourceId,
        event,
        resourceSlotAssignmentEvent: createNewEventObject({
          ...event,
          event_type: 'RESOURCE_SLOT_ASSIGNMENT'
        }),
        rate
      })
    );
  };

  return (
    <>
      <BookResourceFlyoverBookingContents
        slot={slot}
        event={event}
        setEvent={setEvent}
        rate={rate}
        setRate={setRate}
        openCreateBooking={openCreateBooking}
      />
    </>
  );
};

BookResourceFlyoverBooking.propTypes = {
  slot: PropTypes.object,
  resourceId: PropTypes.number
};

export default BookResourceFlyoverBooking;
